<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
    <UiCard class="w-full max-w-md">
      <UiCardHeader class="space-y-1 text-center">
        <UiCardTitle>{{ $t('auth.login.title') }}</UiCardTitle>
        <UiCardDescription>{{ $t('auth.login.description') }}</UiCardDescription>
      </UiCardHeader>
      <UiCardContent class="space-y-4">
        <!-- Error Alert -->
        <UiAlert v-if="serverError" variant="destructive">
          <Icon name="tabler:alert-circle" size="16" />
          <UiAlertDescription>{{ serverError }}</UiAlertDescription>
        </UiAlert>

        <!-- Email/Password Form -->
        <VeeForm :validation-schema="loginSchema" @submit="handleEmailLogin" class="space-y-4" v-slot="{ meta }">
          <div class="space-y-2">
            <UiLabel for="email">{{ $t('auth.fields.email') }}</UiLabel>
            <VeeField name="email" v-slot="{ field, errorMessage }">
              <UiInput
                id="email"
                v-bind="field"
                type="email"
                :placeholder="$ts('auth.fields.emailPlaceholder')"
                autocomplete="email"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <UiLabel for="password">{{ $t('auth.fields.password') }}</UiLabel>
              <NuxtLink
                :to="$localeRoute('/account/forgot-password')"
                class="text-sm text-primary hover:underline"
              >
                {{ $t('auth.login.forgotPassword') }}
              </NuxtLink>
            </div>
            <VeeField name="password" v-slot="{ field, errorMessage }">
              <UiInput
                id="password"
                v-bind="field"
                type="password"
                :placeholder="$ts('auth.fields.passwordPlaceholder')"
                autocomplete="current-password"
              />
              <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
            </VeeField>
          </div>
          <UiButton type="submit" class="w-full" :disabled="isLoading || !meta.valid">
            <Icon v-if="isLoading" name="tabler:loader-2" size="16" class="animate-spin" />
            {{ $t('auth.login.submit') }}
          </UiButton>
        </VeeForm>

        <!-- Divider -->
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <UiSeparator />
          </div>
          <div class="relative flex justify-center text-xs uppercase">
            <span class="bg-card px-2 text-muted-foreground">
              {{ $t('auth.login.orContinueWith') }}
            </span>
          </div>
        </div>

        <!-- Social Login Buttons -->
        <div class="grid grid-cols-2 gap-4">
          <UiButton
            type="button"
            variant="outline"
            @click="handleGoogleLogin"
            :disabled="isLoading"
          >
            <Icon name="devicon:google" size="16" />
            Google
          </UiButton>
          <UiButton
            type="button"
            variant="outline"
            @click="handleAppleLogin"
            :disabled="isLoading"
          >
            <Icon name="devicon:apple" size="16" />
            Apple
          </UiButton>
        </div>

        <!-- Passkey Login -->
        <UiButton
          type="button"
          variant="outline"
          class="w-full"
          @click="handlePasskeyLogin"
          :disabled="isLoading"
        >
          <Icon name="tabler:fingerprint" size="16" />
          {{ $t('auth.login.passkey') }}
        </UiButton>
      </UiCardContent>
      <UiCardFooter class="flex justify-center">
        <p class="text-sm text-muted-foreground">
          {{ $t('auth.login.noAccount') }}
          <NuxtLink :to="$localeRoute('/account/register')" class="text-primary hover:underline">
            {{ $t('auth.login.signUp') }}
          </NuxtLink>
        </p>
      </UiCardFooter>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { definePageMeta, useRoute, useRouter, useI18n, useSeoMeta, useNuxtApp } from '#imports';
import { useAuthClient } from '#composables/useAuthClient';
import { useGoogleOneTap } from '#composables/useGoogleOneTap';
import { usePostHog } from '#composables/usePostHog';
import { primeSessionCache } from '#bubba/shared/utils/sessionCache';

definePageMeta({
  middleware: ['guest'],
});

const { $ts, $localeRoute } = useI18n();
useSeoMeta({ title: () => $ts('auth.login.seo.title') });

const loginSchema = toTypedSchema(
  z.object({
    email: z.string().min(1, $ts('auth.validation.emailRequired')).email($ts('auth.validation.emailInvalid')),
    password: z.string().min(1, $ts('auth.validation.passwordRequired')),
  })
);

const authClient = useAuthClient();
const route = useRoute();
const router = useRouter();
const { $refreshConvexAuth } = useNuxtApp();
const { identifyUser, captureEvent } = usePostHog();

// Resolve returnTo from query param (e.g. /book/slug redirect)
const returnTo = computed(() => {
  const rt = route.query.returnTo;
  if (typeof rt === 'string' && rt.startsWith('/')) return rt;
  return '/account';
});

// Trigger Google One Tap prompt
useGoogleOneTap(returnTo.value);

const isLoading = ref(false);
const serverError = ref<string | null>(null);

// Auto-trigger social login if provider query param is set (from direct booking)
onMounted(() => {
  const provider = route.query.provider;
  if (provider === 'google') {
    handleGoogleLogin();
  } else if (provider === 'apple') {
    handleAppleLogin();
  }
});

async function handleEmailLogin(values: Record<string, unknown>) {
  const email = values.email as string;
  const password = values.password as string;

  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.signIn.email({
      email,
      password,
    });

    if (result.error) {
      serverError.value = result.error.message || 'Login failed. Please check your credentials.';
      return;
    }

    if (result.data?.user) {
      identifyUser(result.data.user.id, {
        email: result.data.user.email,
        name: result.data.user.name,
      });
      captureEvent('user_logged_in', { method: 'email' });
    }

    primeSessionCache(result);
    if (typeof $refreshConvexAuth === 'function') {
      $refreshConvexAuth();
    }
    router.push(returnTo.value);
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'An unexpected error occurred.';
  } finally {
    isLoading.value = false;
  }
}

async function handleGoogleLogin() {
  isLoading.value = true;
  serverError.value = null;

  try {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: returnTo.value,
    });
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Google login failed.';
    isLoading.value = false;
  }
}

async function handleAppleLogin() {
  isLoading.value = true;
  serverError.value = null;

  try {
    await authClient.signIn.social({
      provider: 'apple',
      callbackURL: returnTo.value,
    });
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Apple login failed.';
    isLoading.value = false;
  }
}

async function handlePasskeyLogin() {
  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.signIn.passkey();

    if (result?.error) {
      serverError.value = result.error.message || 'Passkey login failed.';
      return;
    }

    if (result.data?.user) {
      identifyUser(result.data.user.id, {
        email: result.data.user.email,
        name: result.data.user.name,
      });
      captureEvent('user_logged_in', { method: 'passkey' });
    }

    primeSessionCache(result);
    if (typeof $refreshConvexAuth === 'function') {
      $refreshConvexAuth();
    }
    router.push(returnTo.value);
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Passkey login failed.';
  } finally {
    isLoading.value = false;
  }
}
</script>
